import { useState } from 'react';
import {
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Stack,
  Table,
  Tooltip,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';

import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import moment from 'moment';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api } from 'src/models/Api.model';
import { ApplicationInstance } from 'src/models/Application.model';
import {
  isCvdTemplateWithInvalidAge,
  MAX_CVD_AGE,
  MIN_CVD_AGE,
} from 'src/models/CvdLevel.model';
import { DilationEnum } from 'src/models/Dilation.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { Gender } from 'src/models/Gender.model';
import { getLocale, getLocaleCode, Language } from 'src/models/Language.model';
import {
  ReportTemplate,
  ReportTemplateName,
} from 'src/models/ReportTemplate.model';
import { Session } from 'src/models/Session.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useNavigateAfterConfirm from 'src/hooks/useNavigateAfterConfirm';
import useSubmitSession from 'src/hooks/useSubmitSession';
import useUiTranslation from 'src/hooks/useUiTranslation';

import { getMatchedPatient } from 'src/utils/patientMatching';
import {
  namePattern,
  pidPattern,
  validateDatePattern,
  validateNameLength,
  validateNamePattern,
  validatePID,
  validatePIDLength,
} from 'src/utils/validationRules';

import { SearchPatientFormValues } from '../NewPatientHeader/NewPatientHeader';

type NewPatientCreationProps = {
  isOpen: boolean;
  onClose: () => void;
  onMatchedPatient: (formValues: Partial<Session>) => void;
  searchMethods: UseFormReturn<SearchPatientFormValues>;
};

type FormValues = {
  dob: string;
  firstName: string;
  lastName: string;
  id: string;
  isLeftEyeDilated: DilationEnum;
  isRightEyeDilated: DilationEnum;
  template: ReportTemplate;
  gender: Gender;
};

export default function NewPatientCreation({
  isOpen,
  onClose,
  onMatchedPatient,
  searchMethods,
}: NewPatientCreationProps) {
  const { tCommon, language } = useUiTranslation();

  const {
    practiceSettings: { product, camera },
    updateSession,
  } = useRemoteBoundStore();

  const { createPatient, initializeRetinalExam } = useSubmitSession();

  const { handleHttpRequestError } = useHandleHttpRequestError();
  const navigateAfterConfirm = useNavigateAfterConfirm();

  const [matchedPatient, setMatchedPatient] = useState<GalenDataUser>();

  const handleCloseAndReset = () => {
    onClose();
    setMatchedPatient(undefined);
    reset();
  };

  const handleCreateNewPatientAfterMatch = () => {
    setMatchedPatient(undefined);
    reset();
  };
  const handleContinueWithFoundPatient = () => {
    if (!matchedPatient) {
      throw new Error('No matched patient found');
    }
    const tempSession = {
      id: matchedPatient.patientId,
      patientUserId: matchedPatient.userId,
      firstName: matchedPatient.firstName,
      lastName: matchedPatient.lastName,
      dob: matchedPatient.dateOfBirth,
      age: moment().diff(matchedPatient.dateOfBirth, 'years'),
    };
    onMatchedPatient(tempSession);
  };

  const handleGetMatchedPatient = async (data: FormValues) => {
    try {
      return await getMatchedPatient(data);
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);

      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  const getValidatedDefaultValue = (
    value: string | undefined,
    validator: (val: string) => boolean,
  ): string => {
    if (!value) return '';
    return validator(value) ? value : '';
  };

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    control,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      dob: searchMethods.getValues('dob') || '',
      firstName: getValidatedDefaultValue(
        searchMethods.getValues('firstName'),
        (val) => validateNamePattern(val) && validateNameLength(val),
      ),
      lastName: getValidatedDefaultValue(
        searchMethods.getValues('lastName'),
        (val) => validateNamePattern(val) && validateNameLength(val),
      ),
      id: getValidatedDefaultValue(
        searchMethods.getValues('id'),
        (val) => validatePID(val) && validatePIDLength(val),
      ),
      isLeftEyeDilated: DilationEnum.NotDilated,
      isRightEyeDilated: DilationEnum.NotDilated,
      template: ApplicationInstance(product, camera).templates[0],
      gender: Gender.UNSPECIFIED,
    },
  });

  const ValidFieldIcon = (field: keyof FormValues) => {
    return (
      getValues(field) &&
      !errors[field] && (
        <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
      )
    );
  };

  const registerDOB = {
    ...register('dob', {
      required: tCommon('patientFields.dobRequired'),
    }),
    ref: undefined,
  };

  const isContainCVDAlgorithm = watch('template')?.includes('cvd');
  const [showGenderWarning, setShowGenderWarning] = useState(false);

  const handleCreate = () => {
    if (isContainCVDAlgorithm && !watch('gender')) {
      setShowGenderWarning(true);
      return;
    }
    setShowGenderWarning(false);
    handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: FormValues) => {
    const isPatientMatch = await handleGetMatchedPatient(data);
    if (isPatientMatch?.[0]) {
      setMatchedPatient(isPatientMatch[0]);
      return;
    }

    const dob = moment(data.dob).format('YYYY-MM-DD');
    const age = moment().diff(data.dob, 'years');

    const patientUserId = await createPatient({
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      dob,
      id: data.id.trim(),
    });

    const tempSession = {
      patientUserId,
      id: data.id.trim(),
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      dob,
      age,
      gender: data.gender,
      leftEyeDilatedTime:
        data.isLeftEyeDilated === DilationEnum.Dilated ? new Date() : undefined,
      rightEyeDilatedTime:
        data.isRightEyeDilated === DilationEnum.Dilated
          ? new Date()
          : undefined,
      template: getValues('template'),
    };

    updateSession(tempSession);

    await initializeRetinalExam(tempSession);
    navigateAfterConfirm();
  };

  return (
    <CustomModal show={isOpen} onHide={handleCloseAndReset} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {matchedPatient
            ? tCommon('patientFields.newPatientCreationMatchFound')
            : tCommon('patientFields.newPatientCreation')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {matchedPatient ? (
          <>
            <p className="text-danger fw-bold fs-5">
              {tCommon('patientFields.patientAlreadyExists')}
            </p>
            <Table>
              <thead>
                <tr>
                  <th>{tCommon('patientFields.DOB')}</th>
                  <th>{tCommon('patientFields.First Name')}</th>
                  <th>{tCommon('patientFields.Last Name')}</th>
                  <th>{tCommon('patientFields.PID')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{matchedPatient.dateOfBirth}</td>
                  <td>{matchedPatient.firstName}</td>
                  <td>{matchedPatient.lastName}</td>
                  <td>{matchedPatient.patientId}</td>
                </tr>
              </tbody>
            </Table>
          </>
        ) : (
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={3}>
              <Form.Group controlId="dob">
                <Form.Label>
                  {tCommon('patientFields.DOB')} ({tCommon('dateFormat')})
                  {ValidFieldIcon('dob')}
                </Form.Label>

                <Controller
                  control={control}
                  {...registerDOB}
                  render={({ field: { onChange, value } }) => (
                    <div className="position-relative">
                      <DatePicker
                        locale={getLocale(language as Language)}
                        id="dob"
                        autoComplete="off"
                        showIcon={false}
                        wrapperClassName="w-100"
                        className="form-control mb-3"
                        popperClassName="custom-datepicker-popper"
                        calendarClassName="custom-datepicker"
                        placeholderText={tCommon('dateFormat')}
                        dateFormat={['yyyy-MM-dd', 'yyyy/MM/dd', 'yyyy.MM.dd']}
                        maxDate={new Date()}
                        showMonthDropdown
                        showYearDropdown
                        yearDropdownItemNumber={150}
                        scrollableYearDropdown
                        onChange={(date, e) => {
                          const target = e?.target as HTMLInputElement;
                          if (
                            !target.value ||
                            validateDatePattern(target.value)
                          ) {
                            setValue(
                              'dob',
                              moment(value).toDate().toDateString(),
                            );
                            onChange(date);
                            if (
                              isCvdTemplateWithInvalidAge(
                                getValues('template'),
                                moment().diff(date, 'years'),
                              )
                            ) {
                              setValue(
                                'template',
                                ApplicationInstance(product, camera)
                                  .templates[0],
                              );
                            }
                          }
                        }}
                        selected={value ? moment(value).toDate() : undefined}
                      />
                    </div>
                  )}
                />
                {errors.dob?.message && (
                  <p className="text-danger">{errors.dob?.message}</p>
                )}
              </Form.Group>

              <Row>
                <Form.Group as={Col} controlId="firstName">
                  <Form.Label>
                    {tCommon('patientFields.First Name')}
                    {ValidFieldIcon('firstName')}
                  </Form.Label>

                  <Form.Control
                    placeholder={tCommon('patientFields.firstNamePlaceholder')}
                    {...register('firstName', {
                      required: tCommon('patientFields.firstNameRequired'),
                      maxLength: {
                        value: 100,
                        message: tCommon('patientFields.firstNameMaxLength'),
                      },
                      pattern: {
                        value: namePattern,
                        message: tCommon('patientFields.firstNameInvalid'),
                      },
                    })}
                    onChange={(e) => {
                      if (e.target.value) {
                        if (!validateNamePattern(e.target.value)) {
                          setError('firstName', {
                            type: 'pattern',
                            message: tCommon('patientFields.firstNameInvalid'),
                          });
                          return;
                        }

                        if (!validateNameLength(e.target.value)) {
                          setError('firstName', {
                            type: 'maxLength',
                            message: tCommon(
                              'patientFields.firstNameMaxLength',
                            ),
                          });
                          return;
                        }
                      }
                      clearErrors('firstName');

                      setValue('firstName', e.target.value);
                    }}
                    autoComplete="off"
                  />
                  {errors.firstName?.message && (
                    <p className="text-danger">{errors.firstName?.message}</p>
                  )}
                </Form.Group>

                <Form.Group as={Col} controlId="lastName">
                  <Form.Label>
                    {tCommon('patientFields.Last Name')}
                    {ValidFieldIcon('lastName')}
                  </Form.Label>

                  <Form.Control
                    placeholder={tCommon('patientFields.lastNamePlaceholder')}
                    {...register('lastName', {
                      required: tCommon('patientFields.lastNameRequired'),
                      maxLength: {
                        value: 100,
                        message: tCommon('patientFields.lastNameMaxLength'),
                      },
                      pattern: {
                        value: namePattern,
                        message: tCommon('patientFields.lastNameInvalid'),
                      },
                    })}
                    onChange={(e) => {
                      if (e.target.value) {
                        if (!validateNamePattern(e.target.value)) {
                          setError('lastName', {
                            type: 'pattern',
                            message: tCommon('patientFields.lastNameInvalid'),
                          });
                          return;
                        }

                        if (!validateNameLength(e.target.value)) {
                          setError('lastName', {
                            type: 'maxLength',
                            message: tCommon('patientFields.lastNameMaxLength'),
                          });
                          return;
                        }
                      }
                      clearErrors('lastName');

                      setValue('lastName', e.target.value);
                    }}
                    autoComplete="off"
                  />

                  {errors.lastName?.message && (
                    <p className="text-danger">{errors.lastName?.message}</p>
                  )}
                </Form.Group>
              </Row>

              <Form.Group>
                <Form.Label>
                  {tCommon('patientFields.PID')}
                  {ValidFieldIcon('id')}
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder={tCommon('patientFields.pidPlaceholder')}
                  autoComplete="off"
                  {...register('id', {
                    required: tCommon('patientFields.pidRequired'),
                    maxLength: {
                      value: 20,
                      message: tCommon('patientFields.pidMaxLength'),
                    },
                    pattern: {
                      value: pidPattern,
                      message: tCommon('patientFields.pidInvalid'),
                    },
                  })}
                  onChange={(e) => {
                    if (e.target.value) {
                      if (!validatePIDLength(e.target.value)) {
                        setError('id', {
                          type: 'maxLength',
                          message: tCommon('patientFields.pidMaxLength'),
                        });
                        return;
                      }

                      if (!validatePID(e.target.value)) {
                        setError('id', {
                          type: 'pattern',
                          message: tCommon('patientFields.pidInvalid'),
                        });
                        return;
                      }
                    }
                    clearErrors('id');

                    setValue('id', e.target.value);
                  }}
                />
                {errors.id?.message && (
                  <p className="text-danger">{errors.id?.message}</p>
                )}
              </Form.Group>

              <div className="border rounded p-3 shadow">
                <Stack gap={2}>
                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column className="fw-medium">
                      {tCommon('examFields.leftEyeDilation')}
                    </Form.Label>

                    <Col>
                      <Form.Group className="gap-2">
                        <Form.Check
                          type="radio"
                          label={tCommon('examFields.notDilated')}
                          value={DilationEnum.NotDilated}
                          {...register('isLeftEyeDilated')}
                        />
                        <Form.Check
                          type="radio"
                          label={tCommon('examFields.dilated')}
                          value={DilationEnum.Dilated}
                          {...register('isLeftEyeDilated')}
                        />
                      </Form.Group>
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column className="fw-medium">
                      {tCommon('examFields.rightEyeDilation')}
                    </Form.Label>
                    <Col>
                      <Form.Group className="gap-2">
                        <Form.Check
                          type="radio"
                          label={tCommon('examFields.notDilated')}
                          value={DilationEnum.NotDilated}
                          {...register('isRightEyeDilated')}
                        />
                        <Form.Check
                          type="radio"
                          label={tCommon('examFields.dilated')}
                          value={DilationEnum.Dilated}
                          {...register('isRightEyeDilated')}
                        />
                      </Form.Group>
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row}>
                    <Col>
                      <Form.Label className="fw-medium">
                        {tCommon('examFields.selectReportType')}
                      </Form.Label>
                    </Col>
                    <Col>
                      {ApplicationInstance(product, camera).templates.map(
                        (template) =>
                          isCvdTemplateWithInvalidAge(
                            template,
                            moment().diff(watch('dob'), 'years'),
                          ) ? (
                            <OverlayTrigger
                              placement="bottom"
                              overlay={
                                <Tooltip>
                                  {tCommon(
                                    'examFields.cardiovascularAnalysis',
                                    {
                                      minAge: MIN_CVD_AGE,
                                      maxAge: MAX_CVD_AGE,
                                    },
                                  )}
                                </Tooltip>
                              }
                              key={template}
                            >
                              <span className="d-inline-block">
                                <Form.Check
                                  key={template}
                                  type="radio"
                                  label={tCommon(
                                    `report.reportTemplateFields.${ReportTemplateName(template)}`,
                                  )}
                                  value={template}
                                  disabled={true}
                                  {...register('template')}
                                />
                              </span>
                            </OverlayTrigger>
                          ) : (
                            <Form.Check
                              key={template}
                              type="radio"
                              label={tCommon(
                                `report.reportTemplateFields.${ReportTemplateName(template)}`,
                              )}
                              value={template}
                              {...register('template')}
                            />
                          ),
                      )}
                    </Col>
                  </Form.Group>

                  {isContainCVDAlgorithm && (
                    <Form.Group as={Row}>
                      <Col>
                        <Form.Label className="fw-medium">
                          {tCommon(`patientFields.sex`)}
                        </Form.Label>
                      </Col>
                      <Col>
                        <Stack>
                          {Object.values(Gender).map((gender) => (
                            <Form.Check
                              key={gender}
                              type="radio"
                              label={_.capitalize(
                                tCommon(`patientFields.${gender}`),
                              )}
                              value={gender}
                              {...register('gender', {
                                onChange: () => setShowGenderWarning(false),
                              })}
                            />
                          ))}
                        </Stack>
                        {showGenderWarning && (
                          <Form.Text className="text-danger">
                            {tCommon('examFields.patientSexRequired')}
                          </Form.Text>
                        )}
                      </Col>
                    </Form.Group>
                  )}
                </Stack>
              </div>
            </Stack>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        {matchedPatient ? (
          <>
            <Button
              variant="primary-100"
              onClick={handleCreateNewPatientAfterMatch}
            >
              {tCommon('modal.createNewPatient')}
            </Button>
            <Button variant="primary" onClick={handleContinueWithFoundPatient}>
              {tCommon('modal.continueWithFoundPatient')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="primary-100"
              onClick={handleCloseAndReset}
              disabled={isSubmitting}
            >
              {tCommon('modal.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? tCommon('modal.confirming')
                : tCommon('modal.createNewPatient')}
            </Button>
          </>
        )}
      </Modal.Footer>
    </CustomModal>
  );
}
